import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import '../components/css/Validatorinfo.css';
import ValidatorSetupPopup from './ValidatorSetupPopup';
import NodeProgressPopup from './NodeProgressPopup';

const API_BASE = '';

const CHAIN_CONFIG = {
  terra_classic: {
    label: 'Terra Classic (LUNC)', network: 'columbus-5', symbol: 'LUNC',
    setupUrl: '/api/setup-validator', chainHeightUrl: '/api/chain-height',
    nodeHeightUrl: '/api/node-height', operatorUrl: '/api/terravaloper',
    commissionRate: '0.025', commissionMaxRate: '0.025', commissionMaxChangeRate: '0.025',
    hasFeeder: true,
  },
  luna: {
    label: 'Terra (LUNA)', network: 'phoenix-1', symbol: 'LUNA',
    setupUrl: '/api/luna/setup-validator', chainHeightUrl: '/api/luna/chain-height',
    nodeHeightUrl: '/api/luna/node-height', operatorUrl: '/api/luna/terravaloper',
    commissionRate: '0.05', commissionMaxRate: '0.20', commissionMaxChangeRate: '0.01',
    hasFeeder: false,
  },
};

const createFormData = (chainName, profile = {}) => {
  const config = CHAIN_CONFIG[chainName] || CHAIN_CONFIG.terra_classic;
  const base = {
    customerName: [profile.firstName, profile.lastName].filter(Boolean).join(' '),
    validatorName: '',
    commissionRate: config.commissionRate,
    commissionMaxRate: config.commissionMaxRate,
    commissionMaxChangeRate: config.commissionMaxChangeRate,
    validatorDescription: '',
    validatorWebsite: '',
    validatorWalletAddress: '',
    validatorSeedPhrases: '',
    terravaloperAddress: '',
  };

  return config.hasFeeder
    ? { ...base, feederWalletAddress: '', feederSeedPhrases: '' }
    : { ...base, validatorContactEmail: '', selfDelegationAmount: '1' };
};

const LABELS = {
  customerName: 'Customer name',
  validatorName: 'Validator name (spaces allowed)',
  commissionRate: 'Commission rate',
  commissionMaxRate: 'Maximum commission rate',
  commissionMaxChangeRate: 'Maximum daily commission change',
  validatorDescription: 'Validator description',
  validatorWebsite: 'Validator website',
  validatorContactEmail: 'Validator contact email (optional)',
  validatorWalletAddress: 'Validator wallet address',
  validatorSeedPhrases: 'Validator wallet seed phrase',
  feederWalletAddress: 'Oracle feeder wallet address',
  feederSeedPhrases: 'Oracle feeder seed phrase',
  selfDelegationAmount: 'Self-delegation amount (LUNA)',
  terravaloperAddress: 'Validator operator address',
};

const FORM_SECTIONS = [
  {
    title: 'Validator details',
    description: 'Choose how your validator will appear on the network.',
    fields: ['customerName', 'validatorName', 'validatorDescription', 'validatorWebsite', 'validatorContactEmail'],
  },
  {
    title: 'Commission settings',
    description: 'Rates are decimals between 0 and 1. For example, 0.05 means 5%.',
    fields: ['commissionRate', 'commissionMaxRate', 'commissionMaxChangeRate'],
  },
  {
    title: 'Wallet and node',
    description: 'Use an existing Terra wallet or generate one here. Save each recovery phrase before continuing.',
    fields: ['validatorWalletAddress', 'validatorSeedPhrases', 'feederWalletAddress', 'feederSeedPhrases', 'selfDelegationAmount', 'terravaloperAddress'],
  },
];

const Validatorinfo = () => {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(Boolean(token));
  const [profileError, setProfileError] = useState('');
  const [formData, setFormData] = useState(createFormData('terra_classic'));
  const [isLoading, setIsLoading] = useState(false);
  const [blockHeight, setBlockHeight] = useState({ node: 0, chain: 0 });
  const [showValidatorSeed, setShowValidatorSeed] = useState(false);
  const [showFeederSeed, setShowFeederSeed] = useState(false);
  const [generatingWallet, setGeneratingWallet] = useState(null);
  const [walletGenerationError, setWalletGenerationError] = useState('');
  const [generatedWallets, setGeneratedWallets] = useState({});
  const [savedRecovery, setSavedRecovery] = useState({});
  const [syncProgress, setSyncProgress] = useState(0);
  const [blocksRemaining, setBlocksRemaining] = useState(null);
  const [nodeCatchingUp, setNodeCatchingUp] = useState(null);
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [setupFailed, setSetupFailed] = useState(false);
  const [setupMessage, setSetupMessage] = useState('');
  const [showPopup, setShowPopup] = useState(true);
  const [showProgressPopup, setShowProgressPopup] = useState(false);

  const chainName = profile?.chainName;
  const chain = CHAIN_CONFIG[chainName];
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/api/users/me`, { headers: authHeaders });
        if (!active) return;
        const userProfile = response.data || {};
        setProfile(userProfile);
        setProfileError('');
        if (CHAIN_CONFIG[userProfile.chainName]) {
          setFormData(createFormData(userProfile.chainName, userProfile));
        }
      } catch (error) {
        if (active) setProfileError(error.response?.data?.message || 'Unable to load your setup profile.');
      } finally {
        if (active) setProfileLoading(false);
      }
    };

    if (token) {
      loadProfile();
    } else {
      setProfile(null);
      setProfileError('');
      setProfileLoading(false);
    }
    return () => { active = false; };
  }, [token, authHeaders]);

  useEffect(() => {
    if (!chain || !profile?.email) return undefined;
    let active = true;

    const fetchHeights = async () => {
      const [chainResult, nodeResult] = await Promise.allSettled([
        axios.get(`${API_BASE}${chain.chainHeightUrl}`),
        axios.get(`${API_BASE}${chain.nodeHeightUrl}`, {
          params: { email: profile.email }, headers: authHeaders,
        }),
      ]);
      if (!active) return;

      const chainHeight = chainResult.status === 'fulfilled' ? Number(chainResult.value.data?.height || 0) : 0;
      const nodeHeight = nodeResult.status === 'fulfilled' ? Number(nodeResult.value.data?.height || 0) : 0;
      setBlockHeight({ chain: chainHeight, node: nodeHeight });
      setNodeCatchingUp(nodeResult.status === 'fulfilled' ? nodeResult.value.data?.catchingUp ?? null : null);

      const remaining = chainHeight && nodeHeight ? Math.max(chainHeight - nodeHeight, 0) : null;
      setBlocksRemaining(remaining);

      if (chainHeight && nodeHeight) {
        const caughtUp = nodeResult.status === 'fulfilled' && nodeResult.value.data?.catchingUp === false;
        const progress = Number(((nodeHeight / chainHeight) * 100).toFixed(2));
        const hasSyncFlag = nodeResult.status === 'fulfilled' && typeof nodeResult.value.data?.catchingUp === 'boolean';
        setSyncProgress(caughtUp || (!hasSyncFlag && remaining <= 2) ? 100 : Math.min(progress, 99.9));
      } else {
        setSyncProgress(0);
      }
    };

    fetchHeights();
    const interval = setInterval(fetchHeights, 6000);
    return () => { active = false; clearInterval(interval); };
  }, [chain, profile?.email, authHeaders]);

  useEffect(() => {
    if (!chain || !formData.validatorWalletAddress) return undefined;
    let active = true;

    const fetchOperator = async () => {
      try {
        const response = await axios.get(`${API_BASE}${chain.operatorUrl}/${encodeURIComponent(formData.validatorWalletAddress)}`);
        const operator = response.data?.terravaloper;
        if (active && operator && operator !== 'Awaiting creation') {
          setFormData((current) => ({ ...current, terravaloperAddress: operator }));
        }
      } catch {
        if (active) setFormData((current) => ({ ...current, terravaloperAddress: '' }));
      }
    };

    fetchOperator();
    const interval = setInterval(fetchOperator, 60000);
    return () => { active = false; clearInterval(interval); };
  }, [chain, formData.validatorWalletAddress]);

  const validateForm = () => {
    if (Object.keys(generatedWallets).some((kind) => generatedWallets[kind] && !savedRecovery[kind])) {
      return 'Confirm that you have saved the recovery phrase for each generated wallet.';
    }
    const commissionPattern = /^(0(?:\.\d{1,6})?|1(?:\.0{1,6})?)$/;
    const commissionValues = [formData.commissionRate, formData.commissionMaxRate, formData.commissionMaxChangeRate];
    if (!commissionValues.every((value) => commissionPattern.test(value))) {
      return 'Commission fields must be decimal values between 0 and 1, for example 0.05.';
    }
    if (Number(formData.commissionRate) > Number(formData.commissionMaxRate)) {
      return 'The commission rate cannot be higher than the maximum commission rate.';
    }
    const validatorName = formData.validatorName.trim().replace(/\s+/g, ' ');
    if (!validatorName || /[\u0000-\u001f\u007f]/.test(formData.validatorName) || new TextEncoder().encode(validatorName).length > 70) {
      return 'Enter a validator name of up to 70 bytes. Spaces are allowed.';
    }
    if (!/^terra1[0-9a-z]{30,60}$/.test(formData.validatorWalletAddress)) {
      return 'Enter a valid Terra validator wallet address.';
    }

    const seedWords = (value) => value.trim().split(/\s+/).filter(Boolean).length;
    if (![12, 18, 24].includes(seedWords(formData.validatorSeedPhrases))) {
      return 'The validator seed phrase must contain 12, 18, or 24 words.';
    }
    if (chain.hasFeeder && ![12, 18, 24].includes(seedWords(formData.feederSeedPhrases))) {
      return 'The oracle feeder seed phrase must contain 12, 18, or 24 words.';
    }
    if (chainName === 'luna' && (!Number.isFinite(Number(formData.selfDelegationAmount)) || Number(formData.selfDelegationAmount) <= 0)) {
      return 'Enter a self-delegation amount greater than zero.';
    }
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) { setSetupMessage(validationError); return; }

    setIsLoading(true);
    setIsSetupComplete(false);
    setSetupFailed(false);
    setShowPopup(false);
    setShowProgressPopup(true);
    setSetupMessage(`Starting ${chain.label} validator setup. Keep this page open while the node is prepared.`);

    try {
      const response = await axios.post(
        `${API_BASE}${chain.setupUrl}`,
        { ...formData, validatorName: formData.validatorName.trim().replace(/\s+/g, ' '), chainName, ...(chainName === 'terra_classic' ? { email: profile.email } : {}) },
        { headers: authHeaders }
      );

      if (response.data?.terravaloper) {
        setFormData((current) => ({
          ...current,
          terravaloperAddress: response.data.terravaloper,
          validatorSeedPhrases: '',
          ...(current.feederSeedPhrases !== undefined ? { feederSeedPhrases: '' } : {}),
        }));
      }
      setIsSetupComplete(true);
      setSetupMessage(response.data?.message || `${chain.label} validator setup completed successfully.`);
    } catch (error) {
      setSetupFailed(true);
      setSetupMessage(
        error.response?.data?.message ||
        (typeof error.response?.data === 'string' ? error.response.data : '') ||
        'Validator setup failed. Please contact support before trying again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    const kind = name.startsWith('validatorWallet') || name === 'validatorSeedPhrases'
      ? 'validator' : name.startsWith('feeder') ? 'feeder' : null;
    if (kind) {
      setGeneratedWallets((current) => ({ ...current, [kind]: false }));
      setSavedRecovery((current) => ({ ...current, [kind]: false }));
    }
  };

  const handleGenerateWallet = async (kind) => {
    const addressKey = kind === 'validator' ? 'validatorWalletAddress' : 'feederWalletAddress';
    const seedKey = kind === 'validator' ? 'validatorSeedPhrases' : 'feederSeedPhrases';
    if ((formData[addressKey] || formData[seedKey]) &&
        !window.confirm('Replace the current wallet address and recovery phrase? Save the existing phrase first if you need it.')) return;

    setWalletGenerationError('');
    setGeneratingWallet(kind);
    try {
      if (!window.isSecureContext || !window.crypto?.getRandomValues) {
        throw new Error('Wallet generation requires a secure connection and a browser with cryptographic random generation.');
      }
      const [{ DirectSecp256k1HdWallet }, { stringToPath }] = await Promise.all([
        import('@cosmjs/proto-signing'),
        import('@cosmjs/crypto'),
      ]);
      const wallet = await DirectSecp256k1HdWallet.generate(24, {
        prefix: 'terra',
        hdPaths: [stringToPath("m/44'/330'/0'/0/0")],
      });
      const [account] = await wallet.getAccounts();
      if (!account?.address?.startsWith('terra1') || !wallet.mnemonic) throw new Error('Wallet generation did not return a valid Terra account.');
      setFormData((current) => ({ ...current, [addressKey]: account.address, [seedKey]: wallet.mnemonic }));
      setGeneratedWallets((current) => ({ ...current, [kind]: true }));
      setSavedRecovery((current) => ({ ...current, [kind]: false }));
    } catch (error) {
      setWalletGenerationError(error.message || 'Unable to generate a wallet. Please try a secure browser.');
    } finally {
      setGeneratingWallet(null);
    }
  };

  if (!token) {
    return (
      <main className="validator-setup-page">
      <div className="validator-info-container">
        <h1>Validator Node Setup</h1>
        <p className="setup-error">
          Validator setup is linked to a client user account. Sign out of the administrator account and sign in with the client account to continue.
        </p>
      </div>
      </main>
    );
  }
  if (profileError) return <main className="validator-setup-page"><div className="validator-info-container"><p className="setup-error">{profileError}</p></div></main>;
  if (profileLoading || !profile) return <main className="validator-setup-page"><div className="validator-info-container"><p>Loading your validator setup...</p></div></main>;
  if (!chain) {
    return (
      <main className="validator-setup-page">
      <div className="validator-info-container">
        <h1>Validator Node Setup</h1>
        <p className="setup-error">No supported chain has been assigned to your account yet. Please contact the administrator.</p>
      </div>
      </main>
    );
  }

  return (
    <main className="validator-setup-page">
    <div className="validator-info-container">
      {showPopup && <ValidatorSetupPopup chainName={chainName} onClose={() => setShowPopup(false)} />}
      {showProgressPopup && <NodeProgressPopup
        chain={chain}
        blockHeight={blockHeight}
        blocksRemaining={blocksRemaining}
        syncProgress={syncProgress}
        nodeCatchingUp={nodeCatchingUp}
        isLoading={isLoading}
        isSetupComplete={isSetupComplete}
        setupFailed={setupFailed}
        setupMessage={setupMessage}
        onClose={() => setShowProgressPopup(false)}
      />}

      <div className="setup-page-heading">
        <span className="setup-chain-kicker">{chain.network}</span>
        <h1>{chain.label} Validator Node Setup</h1>
        <p className="setup-price">In association with Create Nodes <span aria-hidden="true">·</span> $100 USD per month</p>
      </div>

      <div className="setup-network-status" aria-label="Network status">
        <div className="setup-stat"><span>Blockchain height</span><strong>{blockHeight.chain ? blockHeight.chain.toLocaleString() : 'Waiting...'}</strong></div>
        <div className="setup-stat"><span>Node height</span><strong>{blockHeight.node ? blockHeight.node.toLocaleString() : 'Waiting for setup...'}</strong></div>
      </div>

      {isSetupComplete && <p className="setup-success">Setup complete. Your {chain.symbol} validator is now live.</p>}

      <div className="setup-sync-panel">
        <div className="setup-sync-heading"><strong>Node sync</strong><span>{blockHeight.node && blockHeight.chain ? `${syncProgress}%` : 'Preparing'}</span></div>
        <div className="progress-bar-background">
          <div
            className={`progress-bar-foreground ${!blockHeight.node ? 'indeterminate' : ''}`}
            style={{ width: `${syncProgress}%` }}
          />
        </div>
        {blocksRemaining !== null && blocksRemaining > 0 && <p>{blocksRemaining.toLocaleString()} blocks remaining</p>}
        {syncProgress === 100 && blockHeight.node > 0 && <p>Node is caught up with {chain.network}.</p>}
        <button type="button" className="setup-progress-button" onClick={() => { setShowPopup(false); setShowProgressPopup(true); }}>
          View node progress
        </button>
      </div>

      <form onSubmit={handleSubmit} className="validator-info-form" autoComplete="off">
        {FORM_SECTIONS.map((section) => <section className="setup-form-section" key={section.title}>
          <div className="setup-section-heading"><h2>{section.title}</h2><p>{section.description}</p></div>
          {section.title === 'Wallet and node' && walletGenerationError && <p className="setup-error" role="alert">{walletGenerationError}</p>}
          <div className="setup-fields">{section.fields.filter((key) => Object.prototype.hasOwnProperty.call(formData, key)).map((key) => {
          const value = formData[key];
          const isOperator = key === 'terravaloperAddress';
          const isValidatorSeed = key === 'validatorSeedPhrases';
          const isFeederSeed = key === 'feederSeedPhrases';
          const isSeed = isValidatorSeed || isFeederSeed;
          const seedVisible = (isValidatorSeed && showValidatorSeed) || (isFeederSeed && showFeederSeed);
          const isReadOnly = isOperator || key === 'customerName';
          const walletKind = key === 'validatorWalletAddress' ? 'validator' : key === 'feederWalletAddress' ? 'feeder' : null;
          const recoveryKind = isValidatorSeed ? 'validator' : isFeederSeed ? 'feeder' : null;

          return (
            <div key={key} className={`form-group ${['validatorDescription', 'validatorWalletAddress', 'validatorSeedPhrases', 'feederWalletAddress', 'feederSeedPhrases', 'terravaloperAddress'].includes(key) ? 'form-group-wide' : ''} ${walletKind ? 'wallet-address-field' : ''}`}>
              <label htmlFor={`validator-${key}`}>{LABELS[key] || key}</label>
              <div className="validator-input-row">
                <input
                  id={`validator-${key}`}
                  name={key}
                  value={value || ''}
                  onChange={handleChange}
                  type={isSeed && !seedVisible ? 'password' : key === 'validatorContactEmail' ? 'email' : 'text'}
                  readOnly={isReadOnly}
                  autoComplete="off"
                  placeholder={isOperator && !value ? 'Created during setup' : undefined}
                  required={!isOperator && key !== 'validatorContactEmail'}
                />
                {walletKind && (
                  <button type="button" className="generate-wallet-button" disabled={Boolean(generatingWallet) || isLoading}
                    onClick={() => handleGenerateWallet(walletKind)}>
                    {generatingWallet === walletKind ? 'Generating...' : 'Generate wallet'}
                  </button>
                )}
                {isValidatorSeed && (
                  <button type="button" className="seed-toggle" onClick={() => setShowValidatorSeed((visible) => !visible)}>
                    {showValidatorSeed ? 'Hide' : 'Show'}
                  </button>
                )}
                {isFeederSeed && (
                  <button type="button" className="seed-toggle" onClick={() => setShowFeederSeed((visible) => !visible)}>
                    {showFeederSeed ? 'Hide' : 'Show'}
                  </button>
                )}
              </div>
              {recoveryKind && generatedWallets[recoveryKind] && (
                <div className="generated-wallet-note">
                  <p>This recovery phrase controls the wallet. Write it down and store it securely before leaving this page.</p>
                  <label className="recovery-confirmation">
                    <input type="checkbox" checked={Boolean(savedRecovery[recoveryKind])}
                      onChange={(event) => setSavedRecovery((current) => ({ ...current, [recoveryKind]: event.target.checked }))} />
                    I have saved this recovery phrase
                  </label>
                </div>
              )}
            </div>
          );
        })}</div>
        </section>)}

        {chainName === 'luna' && (
          <p className="setup-warning">
            Use a Phoenix-1 wallet and a seed phrase that is not shared with a Terra Classic validator. The wallet must contain the self-delegation amount plus transaction fees.
          </p>
        )}

        <button type="submit" className="submit-button" disabled={isLoading}>
          {isLoading ? 'Setting up validator...' : formData.terravaloperAddress ? 'Validator created' : `Start ${chain.symbol} Validator Setup`}
        </button>
        {setupMessage && <p className={isSetupComplete ? 'setup-success' : 'setup-message'}>{setupMessage}</p>}
      </form>
    </div>
    </main>
  );
};

export default Validatorinfo;
